import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  IconButton,
  Toolbar,
  TextField,
  Typography,
  Button,
} from "@mui/material";

import { Visibility, VisibilityOff } from "@mui/icons-material";

//hooks
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../hooks/useTenant";

//components
import CustomAppBar from "../customAppBar";
import LoadingScreen from "../../shared_modules/loadingScreen";

import myVideo from "../../images/bellosvideo.mp4";

const Login = () => {
  const navigate = useNavigate();

  const { tenant, status } = useTenant(); //load tenent
  const { login } = useAuth(); //login

  const [searchParams] = useSearchParams();

  //form states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  useEffect(() => {
    const message = searchParams.get("message");

    if (message === "created") {
      setShowAlert({
        show: true,
        message: "Your account has been verified. You can now log in.",
        type: "success",
      });
    }

    if (message === "password_reset") {
      setShowAlert({
        show: true,
        message: "Your password has been updated. You can now log in.",
        type: "success",
      });
    }

    if (message === "server_error") {
      setShowAlert({
        show: true,
        message: "Something went wrong. Please try again.",
        type: "error",
      });
    }

    if (message === "expired") {
      setShowAlert({
        show: true,
        message:
          "This verification link has expired. Please request a new one.",
        type: "error",
      });
    }

    if (message === "invite_expired") {
      setShowAlert({
        show: true,
        message:
          "This staff invite link has expired. Please request a new one.",
        type: "error",
      });
    }

    if (message === "invite_accepted") {
      setShowAlert({
        show: true,
        message: "Your invite has been accepted. You can now log in.",
        type: "success",
      });
    }
  }, [searchParams]);

  if (status === "loading") {
    return <LoadingScreen />;
  }

  const handleLogin = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setShowAlert({ show: false, message: "", type: "" });

    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CustomAppBar tenant={tenant} showLoginButton={false} />

      <Grid
        container
        sx={{ height: "calc(100vh - 64px)", minHeight: "500px", mt: "64px" }}
      >
        <Grid size={{ xs: 12, lg: 7 }}>
          <Box
            sx={{
              backgroundColor: "background.default",
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", lg: "center" },
              flexDirection: "column",
              height: "100%",
              padding: { xs: "30px 20px", lg: "30px 50px" },
            }}
          >
            <Typography variant="h3">Welcome Back</Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 1,
                mb: 4,
                fontWeight: 300,
              }}
            >
              Enter your email and password to access your account
            </Typography>

            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
              }}
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                border: "1px solid",
                borderColor: "customDivider",
                padding: "30px",
                borderRadius: "24px",
                width: { xs: "100%", sm: "450px" },
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              }}
            >
              {showAlert.show === true && (
                <Alert sx={{ mb: 2 }} severity={showAlert.type}>
                  {showAlert.message}
                </Alert>
              )}

              <Typography
                variant="body1"
                sx={{
                  color: "text.primary",
                  fontWeight: 300,
                  pb: 1,
                }}
              >
                Email
              </Typography>
              <TextField
                variant="outlined"
                disabled={isSubmitting}
                label=""
                type="email"
                name="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />

              <Typography
                variant="body1"
                sx={{
                  color: "text.primary",
                  fontWeight: 300,
                  mt: "20px",
                  pb: 1,
                }}
              >
                Password
              </Typography>
              <TextField
                variant="outlined"
                disabled={isSubmitting}
                type={showPassword ? "text" : "password"}
                label=""
                name="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          type="button"
                          disabled={isSubmitting}
                          aria-label={
                            showPassword
                              ? "hide the password"
                              : "display the password"
                          }
                          onClick={() => setShowPassword((show) => !show)}
                          //onMouseDown={handleMouseDownPassword}
                          //onMouseUp={handleMouseUpPassword}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ fontSize: "20px" }} />
                          ) : (
                            <Visibility sx={{ fontSize: "20px" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.primary",
                    fontWeight: 300,
                    mt: 1,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                      textUnderlineOffset: "5px",
                    },
                  }}
                  onClick={() => {
                    navigate("/forgot", { replace: true });
                  }}
                >
                  Forgot your password?
                </Typography>
              </Box>

              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
                sx={{ mt: 5 }}
              >
                {!isSubmitting && "Login"}
                {isSubmitting && (
                  <CircularProgress
                    enableTrackSlot
                    size="30px"
                    aria-label="Loading…"
                    sx={{ color: "text.primary" }}
                  />
                )}
              </Button>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 300,
                  }}
                >
                  Don't have an account?
                </Typography>

                <Typography
                  variant="body2"
                  onClick={() => {
                    navigate("/signup", { replace: true });
                  }}
                  sx={{
                    color: "text.primary",
                    fontWeight: 300,
                    ml: 1,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                      textUnderlineOffset: "5px",
                    },
                  }}
                >
                  Sign Up
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid
          size={5}
          sx={{
            display: { xs: "none", lg: "flex" },
            padding: "15px",
          }}
        >
          <video
            width="100%"
            height="100%"
            controls={false}
            muted={true}
            autoPlay={true}
            loop={true}
            playsInline={true}
            preload="auto"
            poster="image"
            style={{
              objectFit: "cover",
              borderRadius: "15px",
            }}
          >
            <source src={myVideo} type="video/mp4" />
          </video>
        </Grid>
      </Grid>
    </>
  );
};

export default Login;
